import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  seq: string;
  image: string;
  tag: string;
  title: string;
  desc: string;
  to: string;
  cta: string;
}

const slides: Slide[] = [
  {
    seq: "hero-slide-1",
    image:
      "https://readdy.ai/api/search-image?query=Artistic%20cinematic%20portrait%20silhouette%20of%20a%20confident%20figure%20in%20warm%20orange%20rim%20light%20against%20a%20deep%20navy%20gradient%20backdrop%2C%20abstract%20light%20beams%2C%20premium%20editorial%20brand%20hero%20image%20high%20detail&width=1920&height=1080&seq=hero-slide-1&orientation=landscape",
    tag: "最新动态",
    title: "张津瑜受邀出席2026年度品牌影响力峰会",
    desc: "以《个人品牌的下一个十年》为题，分享关于内容、作品与长期主义的完整思考。",
    to: "/news/detail/n001",
    cta: "阅读详情",
  },
  {
    seq: "hero-slide-2",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20urban%20night%20scene%20with%20warm%20orange%20street%20lights%20and%20deep%20navy%20skyline%2C%20soft%20bokeh%20and%20reflective%20wet%20pavement%2C%20moody%20premium%20editorial%20hero%20photography%20wide%20composition&width=1920&height=1080&seq=hero-slide-2&orientation=landscape",
    tag: "重点作品",
    title: "《城市光影》系列视觉大片",
    desc: "用光影记录都市人在明暗之间的呼吸，横跨七座城市、历时四个月的视觉系列。",
    to: "/works/detail/w001",
    cta: "查看作品",
  },
  {
    seq: "hero-slide-3",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20person%20walking%20forward%20on%20an%20open%20road%20at%20golden%20hour%2C%20warm%20backlight%20and%20deep%20navy%20long%20shadows%2C%20minimal%20landscape%2C%20premium%20editorial%20brand%20hero%20image&width=1920&height=1080&seq=hero-slide-3&orientation=landscape",
    tag: "品牌理念",
    title: "在喧嚣中保持清醒，在专业中保留温度",
    desc: "张津瑜个人品牌以真诚的表达连接粉丝与合作伙伴，持续输出有价值的内容与作品。",
    to: "/about",
    cta: "了解品牌",
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStart = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrent(((index % slides.length) + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [current]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(diff) > 50) {
      goTo(diff < 0 ? current + 1 : current - 1);
    }
    touchStart.current = null;
  };

  return (
    <section
      className="relative h-[520px] w-full overflow-hidden md:h-[640px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, index) => (
        <div
          key={slide.seq}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} - 张津瑜个人品牌视觉`}
            title={`${slide.title} + 张津瑜`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/60 to-primary-950/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-semibold uppercase tracking-wider text-primary-950">
                  <i className="ri-flashlight-fill text-sm" />
                  {slide.tag}
                </span>
                <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                  {slide.title}
                </h2>
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                  {slide.desc}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to={slide.to}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-800 transition-colors hover:bg-accent-500 hover:text-primary-950"
                  >
                    {slide.cta}
                    <i className="ri-arrow-right-line text-base" />
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    浏览资讯中心
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.seq}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => goTo(index)}
            className={`h-2 cursor-pointer rounded-full transition-all ${
              index === current ? "w-8 bg-accent-500" : "w-2 bg-background-50/60"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(current - 1)}
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(current + 1)}
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}